import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Wordmark from "./Wordmark";

const LINKS = [
  { href: "#about", label: "About" },
  { href: "#pricing", label: "Pricing" },
  { href: "#work", label: "Our Work" },
  { href: "#join", label: "Join" },
  { href: "#contact", label: "Contact" },
];

export const SiteNav = ({ onBookNow }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="site-nav"
    >
      <div
        className={`mx-auto mt-4 flex max-w-7xl items-center justify-between px-4 md:px-6 transition-all duration-500 ${
          scrolled ? "py-2" : "py-3"
        }`}
      >
        <div
          className={`flex items-center w-full justify-between rounded-full px-4 md:px-6 py-2.5 transition-all duration-500 ${
            scrolled ? "glass" : "bg-transparent"
          }`}
        >
          <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inset-0 rounded-full bg-[#E10600] animate-soft-pulse" />
              <span className="absolute inset-0 rounded-full bg-[#E10600] blur-md opacity-60" />
            </span>
            <Wordmark size="sm" />
          </a>

          <nav className="hidden md:flex items-center gap-7">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-body text-neutral-300 hover:text-white transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={onBookNow}
              data-testid="nav-book-now-button"
              className="btn-3d hidden sm:inline-flex items-center gap-2 rounded-full bg-[#E10600] px-5 py-2 text-sm font-medium text-white"
            >
              <span className="relative z-10">Book Now</span>
              <span aria-hidden className="relative z-10">→</span>
            </button>
            <button
              className="md:hidden p-2 text-white"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="md:hidden mx-4 mt-2 rounded-2xl glass p-4"
            data-testid="nav-mobile-panel"
          >
            <div className="flex flex-col gap-3">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="py-2 text-neutral-200 font-body hover:text-[#E10600]"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onBookNow?.();
                }}
                className="mt-2 inline-flex justify-center rounded-full bg-[#E10600] px-4 py-2 text-sm font-medium text-white"
                data-testid="nav-mobile-book-now"
              >
                Book Now
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default SiteNav;
